import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment } from "react";
import { useDomain } from "../contexts/domain-provider";
import { getPages } from "../data/data-layer";
import { DomainGetDomains200DataItem } from "../gen/model";
import HeadManager from "../services/HeadManager";

export const Route = createFileRoute("/sitemap")({
  beforeLoad: () => {
    return {
      title: "Sitemap",
    };
  },
  head: (ctx) => {
    return HeadManager.routerHead(ctx);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { domain } = useDomain();

  return (
    <div className="p-8">
      <h2>Sitemap</h2>
      <div className="mt-8">
        <CollectionSection domain={domain} />
      </div>
    </div>
  );
}

const getAdminUserName = (user: any) => {
  if (!user) return "-";
  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(" ");
  }
  return user.username || user.email || "-";
};

const formatDate = (dateString?: string) => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return dateString;
  }
};

function CollectionSection({ domain }: { domain: DomainGetDomains200DataItem }) {
  const { data } = useSuspenseQuery(getPages() as any);
  const items = (data as any)?.data || [];

  // Sort entries alphabetically by slug/url
  const sortedItems = [...items].sort((a: any, b: any) => {
    const valA = (a.slug || "").toLowerCase();
    const valB = (b.slug || "").toLowerCase();
    return valA.localeCompare(valB);
  });

  return (
    <section className="py-4 border-t border-zinc-200">
      <div className="mb-4">
        <h3>Pages</h3>
      </div>
      {sortedItems.length > 0 ? (
        <div className="grid grid-cols-7 gap-x-6 gap-y-3 text-sm items-center">
          <div className="font-bold border-b border-zinc-200 pb-2">Title</div>
          <div className="font-bold border-b border-zinc-200 pb-2">URL</div>
          <div className="font-bold border-b border-zinc-200 pb-2">Media</div>
          <div className="font-bold border-b border-zinc-200 pb-2">Created By</div>
          <div className="font-bold border-b border-zinc-200 pb-2">Created At</div>
          <div className="font-bold border-b border-zinc-200 pb-2">Updated By</div>
          <div className="font-bold border-b border-zinc-200 pb-2">Updated At</div>
          {sortedItems.map((item: any) => {
            const url = `https://${domain.name}${item.slug}`;
            const image = item.preview_image?.image?.url;

            return (
              <Fragment key={item.documentId}>
                <div className="py-1">
                  {item.title || item.name || "Untitled"}
                </div>
                <div className="py-1 truncate max-w-[200px]" title={url}>
                  <Link
                    to={url as any}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {item.slug || "/"}
                  </Link>
                </div>
                <div className="py-1">
                  {image ? (
                    <a
                      href={image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-600 hover:text-zinc-950 underline"
                    >
                      Image is set
                    </a>
                  ) : (
                    <span className="text-red-500 font-medium">
                      No preview image
                    </span>
                  )}
                </div>
                <div className="py-1 text-zinc-600">
                  {getAdminUserName(item.createdBy)}
                </div>
                <div className="py-1 text-zinc-500 text-xs">
                  {formatDate(item.createdAt)}
                </div>
                <div className="py-1 text-zinc-600">
                  {getAdminUserName(item.updatedBy)}
                </div>
                <div className="py-1 text-zinc-500 text-xs">
                  {formatDate(item.updatedAt)}
                </div>
              </Fragment>
            );
          })}
        </div>
      ) : (
        <div>
          No items found.
        </div>
      )}
    </section>
  );
}

